/**
 * The Change plan dialog's twin of the server's `lib/billingTerms.ts` (PR 2,
 * docs/PLAYBOOK-account-overview.md): the same request, the same refusals word
 * for word, the same preview. Both run `shared/billingTermsCases.json`, so a
 * warning the dialog shows is the sentence the server would refuse with, and
 * Apply is off for exactly the reasons Apply would fail.
 *
 * Kept a copy on purpose (the theme parity pair is the pattern): the dialog
 * never waits on a round trip to know what a change does. Change the server's
 * file and this one together; the cases file is where they meet.
 */

export type Interval = 'month' | 'year';

/** When a change without a free period starts. */
export type When = 'now' | 'renewal' | 'now_bill_at_renewal';

export type PercentDuration = { months: number } | { until: string } | 'forever';

export type PriceTerms =
  | { mode: 'list' }
  | { mode: 'discount'; percent: number; duration: PercentDuration; note: string | null }
  | { mode: 'agreed'; cents: number; note: string | null };

export interface FreePeriodTerms {
  /** the plan they have while it runs */
  planId: number;
  /** a calendar day, YYYY-MM-DD — the day the first charge (or the drop) lands */
  endsOn: string;
  /** convert: the terms above start that day; free: the subscription ends that day */
  after: 'convert' | 'free';
  /** internal, never shown to the customer */
  reason: string | null;
  /** email the customer now, and before it ends */
  notify: boolean;
}

export interface BillingTerms {
  planId: number;
  interval: Interval;
  price: PriceTerms;
  freePeriod: FreePeriodTerms | null;
  /** ignored when there is a free period: it starts now and the rest follows it */
  when: When;
}

/** The request body, in the handoff's names — what `parseBillingTerms` reads back. */
export function bodyOf(t: BillingTerms): Record<string, unknown> {
  const p = t.price;
  return {
    planId: t.planId,
    interval: t.interval,
    priceMode: p.mode,
    ...(p.mode === 'discount' ? { discountPct: p.percent, discountDuration: p.duration, discountNote: p.note } : {}),
    ...(p.mode === 'agreed' ? { agreedCents: p.cents, agreedNote: p.note } : {}),
    freePeriod: t.freePeriod,
    when: t.when,
  };
}

/** The longest a percent may run for by months; forever is its own choice. */
export const MAX_PERCENT_MONTHS = 36;
export const MAX_AGREED_NOTE = 200;
export const MAX_DISCOUNT_NOTE = 500;
export const MAX_FREE_REASON = 500;

// ---------------------------------------------------------------------------
// Days and instants.
//
// The days staff pick — a free period's end, a discount's until — are
// calendar days, stored at 12:00 UTC: the same day from UTC−11 to UTC+11, so
// "ends on Oct 31" is Oct 31 to everybody who reads it (PR 1's lesson, when a
// deletion instant read as the day after in the US).
// ---------------------------------------------------------------------------

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** A real calendar day, YYYY-MM-DD. */
export function isDay(v: unknown): v is string {
  if (typeof v !== 'string' || !DAY_RE.test(v)) return false;
  const d = new Date(`${v}T12:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}

/** The instant a picked day stands for: noon UTC. */
export const dayInstant = (day: string): string => `${day}T12:00:00.000Z`;

/** `day` plus `months` calendar months, clamped to a shorter month's end (Jan 31 + 1 = Feb 28). */
export function addMonthsDay(day: string, months: number): string {
  const [y, m, d] = day.split('-').map(Number);
  const target = m - 1 + months;
  const lastDay = new Date(Date.UTC(y, target + 1, 0)).getUTCDate();
  const out = new Date(Date.UTC(y, target, Math.min(d, lastDay)));
  return out.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Reading the request.
// ---------------------------------------------------------------------------

const wholeNumber = (v: unknown): number =>
  typeof v === 'number' ? v : typeof v === 'string' && /^\s*\d+\s*$/.test(v) ? Number(v) : NaN;

const text = (v: unknown, max: number): string | null | undefined => {
  if (v === undefined || v === null) return null;
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  if (t.length > max) return undefined;
  return t || null;
};

export type ParsedTerms = { terms: BillingTerms } | { status: 400 | 422; error: string };

/**
 * The request body, in the handoff's names (README "API"): `planId`,
 * `interval`, `priceMode` (list | discount | agreed), `discountPct`,
 * `discountDuration` ({months} | {until} | 'forever'), `discountNote` (ours:
 * the percent's internal note, which the old percent modal kept),
 * `agreedCents`, `agreedNote`, `freePeriod` ({planId, endsOn, after, reason,
 * notify} or null) and `when`.
 *
 * A body that sets a percent AND an agreed price is a 422 whatever its
 * `priceMode` says: one discount at a time, and a request that carries both
 * was made by something that did not know that.
 */
export function parseBillingTerms(body: Record<string, unknown> | null | undefined): ParsedTerms {
  const b = body ?? {};
  const planId = wholeNumber(b.planId);
  if (!Number.isInteger(planId) || planId < 1) return { status: 400, error: 'Pick a plan.' };
  if (b.interval !== 'month' && b.interval !== 'year') return { status: 400, error: 'interval must be month or year' };
  const interval = b.interval;

  const hasPct = b.discountPct !== undefined && b.discountPct !== null && b.discountPct !== '';
  const hasAgreed = b.agreedCents !== undefined && b.agreedCents !== null && b.agreedCents !== '';
  if (hasPct && hasAgreed) {
    return {
      status: 422,
      error: 'One discount at a time — a percent off and an agreed price cannot be set together.',
    };
  }

  let price: PriceTerms;
  switch (b.priceMode) {
    case 'list':
      price = { mode: 'list' };
      break;
    case 'discount': {
      const percent = wholeNumber(b.discountPct);
      if (!Number.isInteger(percent)) return { status: 400, error: 'A discount must be between 1% and 100%.' };
      const d = b.discountDuration;
      let duration: PercentDuration;
      if (d === 'forever') {
        duration = 'forever';
      } else if (d && typeof d === 'object' && 'months' in d) {
        const months = wholeNumber((d as { months: unknown }).months);
        if (!Number.isInteger(months)) return { status: 400, error: 'Enter how many months the discount lasts.' };
        duration = { months };
      } else if (d && typeof d === 'object' && 'until' in d) {
        const until = (d as { until: unknown }).until;
        if (!isDay(until)) return { status: 400, error: 'The discount needs an end date.' };
        duration = { until };
      } else {
        return { status: 400, error: 'Say how long the discount lasts: for some months, until a date, or forever.' };
      }
      const note = text(b.discountNote, MAX_DISCOUNT_NOTE);
      if (note === undefined) return { status: 400, error: `A discount note is at most ${MAX_DISCOUNT_NOTE} characters.` };
      price = { mode: 'discount', percent, duration, note };
      break;
    }
    case 'agreed': {
      const cents = wholeNumber(b.agreedCents);
      if (!Number.isInteger(cents)) return { status: 400, error: 'Enter the agreed price.' };
      const note = text(b.agreedNote, MAX_AGREED_NOTE);
      if (note === undefined) return { status: 400, error: `A contract note is at most ${MAX_AGREED_NOTE} characters.` };
      price = { mode: 'agreed', cents, note };
      break;
    }
    default:
      return { status: 400, error: 'priceMode must be list, discount or agreed' };
  }

  let freePeriod: FreePeriodTerms | null = null;
  if (b.freePeriod !== undefined && b.freePeriod !== null) {
    if (typeof b.freePeriod !== 'object') return { status: 400, error: 'freePeriod must be an object or null' };
    const f = b.freePeriod as Record<string, unknown>;
    const fPlan = wholeNumber(f.planId);
    if (!Number.isInteger(fPlan) || fPlan < 1) return { status: 400, error: 'Pick the plan they get during the free period.' };
    if (!isDay(f.endsOn)) return { status: 400, error: 'The free period needs an end date.' };
    if (f.after !== 'convert' && f.after !== 'free') return { status: 400, error: 'Say what happens when the free period ends.' };
    const reason = text(f.reason, MAX_FREE_REASON);
    if (reason === undefined) return { status: 400, error: `The reason is at most ${MAX_FREE_REASON} characters.` };
    freePeriod = { planId: fPlan, endsOn: f.endsOn as string, after: f.after, reason, notify: f.notify !== false };
  }

  const when = b.when === undefined || b.when === null ? 'now' : b.when;
  if (when !== 'now' && when !== 'renewal' && when !== 'now_bill_at_renewal') {
    return { status: 400, error: 'when must be now, renewal or now_bill_at_renewal' };
  }

  return { terms: { planId, interval, price, freePeriod, when } };
}

// ---------------------------------------------------------------------------
// What the account is on now, and the plans on offer — the context every
// check below reads. Built by the route from the account, its standing free
// period and Stripe; by the dialog from the billing view.
// ---------------------------------------------------------------------------

export interface TermsPlan {
  id: number;
  key: string;
  name: string;
  sort_order: number;
  trialable: boolean;
  retired_at: string | null;
  price_monthly_cents: number | null;
  price_annual_cents: number | null;
  currency: string;
}

export interface StandingFreePeriod {
  planId: number;
  /** its end, an instant */
  expiresAt: string;
  after: 'convert' | 'free';
  notify: boolean;
  reason: string | null;
  /** when it started, an instant — what "at most N days" is counted from */
  startsAt?: string | null;
}

/**
 * What the person making the change may give away (Platform › Rules; Ben,
 * 2026-10-09): the longest free period, counted from its start, and the
 * largest percent-off. Null for a Super Admin, whom neither binds.
 */
export interface CompLimits {
  freeDays: number;
  percent: number;
  /**
   * The least an agreed price may be, as a percent of the plan's standard
   * price for the interval (Ben, 2026-10-09). An agreed price was bounded
   * only above: the percent-off stopped at its limit, and an agreed price of
   * a dollar did the same thing unbounded. Absent: no floor.
   */
  agreedFloorPercent?: number;
}

/** The floor in cents for one list price: never less than a cent, rounded up so the percent is honoured. */
export const agreedFloorCents = (listCents: number, floorPercent: number): number =>
  Math.max(1, Math.ceil((listCents * floorPercent) / 100));

/** The price that stands today: an agreed price, a percent, or neither. */
export interface StandingPrice {
  agreedCents: number | null;
  agreedNote: string | null;
  percent: number | null;
  percentMonths: number | null;
  /** when a running percent stops — its end, an instant; null for forever */
  percentEndsAt: string | null;
  /** the percent's own note — internal, never shown to the customer */
  percentNote: string | null;
}

export interface TermsContext {
  /** "now", in ms since the epoch */
  now: number;
  /** today as the reader counts days, YYYY-MM-DD */
  today: string;
  plans: TermsPlan[];
  /**
   * The terms they are on — after any change already waiting (a change at the
   * renewal, what a free period converts to), since that is what the dialog
   * opens on and what Apply replaces.
   */
  planId: number;
  interval: Interval;
  price: StandingPrice;
  /**
   * The plan they PAY for: the free period's restore point while one runs
   * (a Stripe-backed period mirrors its own plan onto the account), else
   * `planId`. A free period must be for a plan above this one.
   */
  paidPlanId: number;
  freePeriod: StandingFreePeriod | null;
  hasSubscription: boolean;
  /** the renewal as a day, when there is a period to renew */
  renewalDay: string | null;
  hasCard: boolean;
  stripe: boolean;
  billingEmail: boolean;
  deleting: boolean;
  /** what they are worth a month now, cents — the preview's "from" */
  mrrCents: number;
  /** absent or null: nothing bounds what this person may give */
  limits?: CompLimits | null;
}

export const listCentsOf = (plan: Pick<TermsPlan, 'price_monthly_cents' | 'price_annual_cents'>, interval: Interval): number | null =>
  (interval === 'year' ? plan.price_annual_cents : plan.price_monthly_cents);

/** A plan that costs something at this interval. */
export const isPaid = (plan: TermsPlan, interval: Interval): boolean => (listCentsOf(plan, interval) ?? 0) > 0;

/** What they pay each period on these terms, before any credit. */
export function perCentsOf(price: PriceTerms, listCents: number | null): number | null {
  if (listCents === null) return null;
  if (price.mode === 'agreed') return price.cents;
  if (price.mode === 'discount') return Math.round((listCents * (100 - price.percent)) / 100);
  return listCents;
}

export const monthlyOf = (cents: number, interval: Interval): number =>
  (interval === 'year' ? Math.round(cents / 12) : cents);

/**
 * The first charge on these terms, as a day: the free period's end when it
 * converts, today when the change is made now, and the renewal when it waits
 * for one (or is billed at one). Null when nothing is ever charged — a free
 * period that drops to Free.
 */
export function firstChargeDay(t: BillingTerms, ctx: TermsContext): string | null {
  if (t.freePeriod) return t.freePeriod.after === 'convert' ? t.freePeriod.endsOn : null;
  if (t.when === 'now') return ctx.today;
  return ctx.renewalDay ?? ctx.today;
}

/** When a discount on these terms stops, as a day; null for forever, or when nothing is charged. */
export function discountEndDay(t: BillingTerms, ctx: TermsContext): string | null {
  if (t.price.mode !== 'discount' || t.price.duration === 'forever') return null;
  const first = firstChargeDay(t, ctx);
  if (!first) return null;
  return 'months' in t.price.duration ? addMonthsDay(first, t.price.duration.months) : t.price.duration.until;
}

// ---------------------------------------------------------------------------
// The refusals. Every sentence here is also an amber warning in the dialog
// (the twin), so a request the server would refuse cannot be applied.
// ---------------------------------------------------------------------------

export const REFUSAL = {
  deleting: 'This account is waiting to be deleted. Restore it before changing what it pays.',
  noPlan: 'That plan does not exist.',
  freeNoPrice: (name: string) => `${name} has no price to discount or replace.`,
  noIntervalPrice: (name: string, interval: Interval) => `${name} has no ${interval === 'year' ? 'annual' : 'monthly'} price.`,
  retired: (name: string) => `${name} is retired. It stays on the accounts already on it, but nobody can be moved onto it.`,
  percentRange: 'A discount must be between 1% and 100%.',
  percentAbove: (max: number) => `A discount above ${max}% needs a Super Admin.`,
  months: 'Enter how many months the discount lasts.',
  monthsMax: `A discount can run for at most ${MAX_PERCENT_MONTHS} months — choose Forever for longer.`,
  annualYears: 'On annual billing a discount runs for whole years — the next 1, 2 or 3 renewals.',
  untilPast: 'The discount end date must be in the future.',
  untilBeforeFirst: (day: string) => `The discount would end before their first charge on ${longDay(day)}.`,
  agreed: 'Enter the agreed price.',
  agreedAbove: 'An agreed price above the list price cannot be applied — Stripe discounts, it does not surcharge.',
  agreedBelow: (percent: number, name: string, floor: string) =>
    `An agreed price below ${percent}% of ${name}’s standard price (${floor}) needs a Super Admin.`,
  freeEnd: 'The free period must end in the future.',
  freeTooLong: (max: number) => `A free period longer than ${max} days from its start needs a Super Admin.`,
  unpriced: (name: string) => `${name} has no price, so nothing can bill it. Give the plan a price first, or use a free period.`,
  freeNotAbove: (current: string) => `A free period must be for a plan above ${current}.`,
  freeNotOffered: (name: string) => `${name} is not offered as a free upgrade period.`,
  runningEndsNow: 'A free period is running. Changing without one ends it today — choose Now, prorated.',
  noRenewal: 'They have no subscription, so there is no renewal to wait for — choose Now.',
  intervalBilledNow: 'Moving between monthly and annual is billed the day it is made — choose Now, prorated or At the next renewal.',
  annualToMonthlyNow: 'Annual to monthly waits for their renewal, so the year they paid for is not handed back — choose At the next renewal.',
  billingEmail: 'Set a billing email first — this change is emailed to the customer, and this account has nowhere to send it.',
  card: (name: string) => `${name} is a paid plan and this account has no card on file. Send them a Billing Portal link to add one, then make the change.`,
  cardConvert: (day: string) => `Converting to paid on ${longDay(day)} needs a card on file. Send them a Billing Portal link first, or choose Drop to Free.`,
  percentNoSubscription: 'There is no live subscription to attach a discount to yet. Add it once they subscribe.',
  unbilled: (name: string) => `They have no subscription, so nothing would bill ${name}. Start a free period that converts — it starts their subscription — or have them subscribe from their Billing page.`,
  nothing: 'Nothing to change — these are the terms they are on.',
} as const;

const planOf = (ctx: TermsContext, id: number): TermsPlan | undefined => ctx.plans.find((p) => p.id === id);

/**
 * Every reason these terms cannot be applied, in the order a person fixes
 * them. Empty means Apply may go ahead — except "nothing to change", which is
 * `sameAsNow`'s to say (the dialog shows it as a disabled Apply, not a warning).
 */
export function refusalsOf(t: BillingTerms, ctx: TermsContext): string[] {
  const out: string[] = [];
  if (ctx.deleting) return [REFUSAL.deleting];
  const plan = planOf(ctx, t.planId);
  if (!plan) return [REFUSAL.noPlan];
  const list = listCentsOf(plan, t.interval);
  const paid = (list ?? 0) > 0;

  if (plan.retired_at && plan.id !== ctx.planId) out.push(REFUSAL.retired(plan.name));
  if (list === null && t.interval === 'year' && plan.price_monthly_cents !== null) out.push(REFUSAL.noIntervalPrice(plan.name, 'year'));

  // The price.
  if (!paid && t.price.mode !== 'list') {
    out.push(REFUSAL.freeNoPrice(plan.name));
  } else if (t.price.mode === 'discount') {
    const p = t.price;
    if (!(p.percent >= 1 && p.percent <= 100)) out.push(REFUSAL.percentRange);
    // The one that runs keeps the percent it was given, whoever gave it.
    else if (ctx.limits && p.percent > ctx.limits.percent && p.percent !== ctx.price.percent) out.push(REFUSAL.percentAbove(ctx.limits.percent));
    if (p.duration !== 'forever' && 'months' in p.duration) {
      const m = p.duration.months;
      if (!(m >= 1)) out.push(REFUSAL.months);
      else if (m > MAX_PERCENT_MONTHS) out.push(REFUSAL.monthsMax);
      else if (t.interval === 'year' && m % 12 !== 0) out.push(REFUSAL.annualYears);
    }
    if (p.duration !== 'forever' && 'until' in p.duration) {
      const until = p.duration.until;
      const first = firstChargeDay(t, ctx);
      if (Date.parse(dayInstant(until)) <= ctx.now) out.push(REFUSAL.untilPast);
      else if (first && until <= first) out.push(REFUSAL.untilBeforeFirst(first));
    }
  } else if (t.price.mode === 'agreed') {
    if (!(t.price.cents >= 1)) out.push(REFUSAL.agreed);
    else if (list !== null && t.price.cents > list) out.push(REFUSAL.agreedAbove);
    else if (list !== null && ctx.limits?.agreedFloorPercent) {
      const floor = agreedFloorCents(list, ctx.limits.agreedFloorPercent);
      // The one that stands keeps the price it was given, whoever gave it —
      // on the plan and interval it was agreed for, and nowhere else.
      const kept = t.price.cents === ctx.price.agreedCents && t.planId === ctx.planId && t.interval === ctx.interval;
      if (t.price.cents < floor && !kept) {
        out.push(REFUSAL.agreedBelow(ctx.limits.agreedFloorPercent, plan.name, moneyWords(floor, plan.currency)));
      }
    }
  }

  // The free period.
  const f = t.freePeriod;
  if (f) {
    const fPlan = planOf(ctx, f.planId);
    // The one that runs may carry on as it is, whatever the offer is today.
    const running = !!ctx.freePeriod && ctx.freePeriod.planId === f.planId;
    if (!fPlan) out.push(REFUSAL.noPlan);
    else if (!running) {
      // Above the plan they pay for — not above a free period that runs,
      // which is where they are now but not what they are on.
      const base = planOf(ctx, ctx.paidPlanId);
      if (base && fPlan.sort_order <= base.sort_order) out.push(REFUSAL.freeNotAbove(base.name));
      else if (!fPlan.trialable) out.push(REFUSAL.freeNotOffered(fPlan.name));
    }
    const ends = Date.parse(dayInstant(f.endsOn));
    if (ends <= ctx.now) out.push(REFUSAL.freeEnd);
    else if (ctx.limits) {
      // Counted from the start, so extending one cannot outrun the limit; one
      // kept as it is, or cut short, is not a new gift.
      const standing = running && ctx.freePeriod ? ctx.freePeriod : null;
      const kept = !!standing && ends <= Date.parse(standing.expiresAt) + 86_400_000;
      const from = standing?.startsAt ? Date.parse(standing.startsAt) : ctx.now;
      if (!kept && Math.ceil((ends - from) / 86_400_000) > ctx.limits.freeDays) out.push(REFUSAL.freeTooLong(ctx.limits.freeDays));
    }
  } else if (ctx.freePeriod && t.when !== 'now') {
    out.push(REFUSAL.runningEndsNow);
  } else if (t.when !== 'now' && !ctx.hasSubscription) {
    out.push(REFUSAL.noRenewal);
  }
  // Ben, 2026-09-27: a paid plan is paid for. With no subscription, only a
  // free period (whose schedule starts one) can put them on a paid plan.
  if (ctx.stripe && !f && !ctx.hasSubscription && paid) out.push(REFUSAL.unbilled(plan.name));
  // Ben, 2026-10-09: that holds for a plan nobody priced too — every plan but
  // Free must be paid for, and this one cannot be billed at all.
  if (ctx.stripe && !f && !paid && plan.key !== 'free' && t.planId !== ctx.planId) out.push(REFUSAL.unpriced(plan.name));
  if (!f && t.when === 'now_bill_at_renewal' && t.interval !== ctx.interval) out.push(REFUSAL.intervalBilledNow);
  // Ben, 2026-09-25: annual → monthly moves at the renewal — made now, the
  // unused year would come back as a credit, and the system does not hand
  // money back systematically. Only on a subscription: there is nothing paid
  // for to hand back otherwise.
  if (!f && t.when === 'now' && ctx.hasSubscription && ctx.interval === 'year' && t.interval === 'month') {
    out.push(REFUSAL.annualToMonthlyNow);
  }

  // Who is told, and who can be charged. A card is needed where something
  // starts charging: a move onto a paid plan or interval, a free period that
  // converts, or one that stops today — not a new price on the plan they have.
  const notifies = !f || f.notify;
  if (notifies && !ctx.billingEmail) out.push(REFUSAL.billingEmail);
  if (ctx.stripe && paid && !ctx.hasCard) {
    const moving = t.planId !== ctx.planId || t.interval !== ctx.interval;
    if (f && f.after === 'convert') out.push(REFUSAL.cardConvert(f.endsOn));
    else if (!f && (moving || !!ctx.freePeriod)) out.push(REFUSAL.card(plan.name));
  }
  if (ctx.stripe && !f && !ctx.hasSubscription && t.price.mode === 'discount') out.push(REFUSAL.percentNoSubscription);

  return out;
}

/** Nothing to push to: no Stripe, or no subscription and no free period to start one. */
export const writtenHereOnly = (t: BillingTerms, ctx: TermsContext): boolean =>
  !t.freePeriod && (!ctx.stripe || !ctx.hasSubscription);

// ---------------------------------------------------------------------------
// Sameness: what the dialog opens on, sent back untouched, changes nothing.
// ---------------------------------------------------------------------------

/** The day an instant falls on, in UTC — how a stored end is compared with a picked day. */
const utcDay = (instant: string): string => new Date(instant).toISOString().slice(0, 10);

/**
 * These terms, as the price that stands today. A running repeating percent
 * comes back from the dialog as "until its end" (that is how it pre-fills), so
 * a percent with the same figure and the same end is the same percent — kept,
 * not restarted.
 */
export function samePrice(p: PriceTerms, s: StandingPrice): boolean {
  if (p.mode === 'list') return s.agreedCents === null && s.percent === null;
  if (p.mode === 'agreed') return s.percent === null && s.agreedCents === p.cents && (s.agreedNote ?? null) === (p.note ?? null);
  if (s.agreedCents !== null || s.percent !== p.percent || (s.percentNote ?? null) !== (p.note ?? null)) return false;
  if (p.duration === 'forever') return s.percentEndsAt === null;
  if ('until' in p.duration) return !!s.percentEndsAt && utcDay(s.percentEndsAt) === p.duration.until;
  return false; // "for N months" always starts afresh
}

export function sameFreePeriod(f: FreePeriodTerms | null, s: StandingFreePeriod | null): boolean {
  if (!f || !s) return !f && !s;
  return f.planId === s.planId && utcDay(s.expiresAt) === f.endsOn && f.after === s.after
    && f.notify === s.notify && (f.reason ?? null) === (s.reason ?? null);
}

/** True when applying these terms would change nothing. */
export function sameAsNow(t: BillingTerms, ctx: TermsContext): boolean {
  return t.planId === ctx.planId && t.interval === ctx.interval
    && samePrice(t.price, ctx.price) && sameFreePeriod(t.freePeriod, ctx.freePeriod);
}

// ---------------------------------------------------------------------------
// The preview: what they will be charged and when, in data. Both sides format
// it their own way; the shared cases pin the data.
// ---------------------------------------------------------------------------

export type TimelineKind =
  | 'free_starts' | 'free_continues' | 'first_charge' | 'drops_to_free'
  | 'now_prorated' | 'now_unbilled' | 'billed_at_renewal' | 'at_renewal' | 'discount_ends'
  /** saved here and in force today, with no subscription to charge */
  | 'now_local';

export interface TimelineEntry {
  /** 'today', or a day */
  at: string;
  kind: TimelineKind;
  /** the plan named in the line, when it names one */
  planId: number | null;
  /** the amount on the right, per period; 0 for free */
  cents: number;
}

export interface PreviewLine {
  kind: 'list' | 'discount' | 'agreed';
  /** list: the plan's list price; discount: the amount off (positive); agreed: the agreed price */
  cents: number;
  percent?: number;
  note?: string | null;
  /** the list price struck through, because an agreed price replaces it */
  struck?: boolean;
}

export interface TermsPreview {
  planId: number;
  interval: Interval;
  currency: string;
  lines: PreviewLine[];
  /** what they pay each period on these terms, or null for a plan with no price */
  totalCents: number | null;
  firstCharge: string | null;
  discountEnds: string | null;
  timeline: TimelineEntry[];
  mrr: { fromCents: number; toCents: number; fromDay: string | null; drops: boolean };
}

export function previewOf(t: BillingTerms, ctx: TermsContext): TermsPreview | null {
  const plan = planOf(ctx, t.planId);
  if (!plan) return null;
  const list = listCentsOf(plan, t.interval);
  const total = perCentsOf(t.price, list);
  const paid = (list ?? 0) > 0;

  const lines: PreviewLine[] = [];
  if (list !== null) lines.push({ kind: 'list', cents: list, ...(t.price.mode === 'agreed' ? { struck: true } : {}) });
  if (t.price.mode === 'discount' && list !== null && total !== null) {
    lines.push({ kind: 'discount', cents: list - total, percent: t.price.percent });
  }
  if (t.price.mode === 'agreed') lines.push({ kind: 'agreed', cents: t.price.cents, note: t.price.note });

  const timeline: TimelineEntry[] = [];
  const f = t.freePeriod;
  const running = !!ctx.freePeriod && !!f && ctx.freePeriod.planId === f.planId;
  if (f) {
    timeline.push({ at: 'today', kind: running ? 'free_continues' : 'free_starts', planId: f.planId, cents: 0 });
    timeline.push(f.after === 'convert'
      ? { at: f.endsOn, kind: 'first_charge', planId: t.planId, cents: total ?? 0 }
      : { at: f.endsOn, kind: 'drops_to_free', planId: null, cents: 0 });
  } else if (writtenHereOnly(t, ctx)) {
    timeline.push({ at: 'today', kind: 'now_local', planId: t.planId, cents: total ?? 0 });
  } else if (t.when === 'now') {
    timeline.push({ at: 'today', kind: 'now_prorated', planId: t.planId, cents: total ?? 0 });
  } else if (t.when === 'now_bill_at_renewal') {
    timeline.push({ at: 'today', kind: 'now_unbilled', planId: t.planId, cents: 0 });
    timeline.push({ at: ctx.renewalDay ?? ctx.today, kind: 'billed_at_renewal', planId: t.planId, cents: total ?? 0 });
  } else {
    timeline.push({ at: ctx.renewalDay ?? ctx.today, kind: 'at_renewal', planId: t.planId, cents: total ?? 0 });
  }
  const local = writtenHereOnly(t, ctx);
  const discountEnds = paid && !local ? discountEndDay(t, ctx) : null;
  if (discountEnds && list !== null) timeline.push({ at: discountEnds, kind: 'discount_ends', planId: t.planId, cents: list });

  const drops = !!f && f.after === 'free';
  return {
    planId: t.planId,
    interval: t.interval,
    currency: plan.currency,
    lines,
    totalCents: total,
    firstCharge: paid && !local ? firstChargeDay(t, ctx) : null,
    discountEnds,
    timeline,
    mrr: {
      fromCents: ctx.mrrCents,
      toCents: drops || total === null ? 0 : monthlyOf(total, t.interval),
      fromDay: f ? f.endsOn : null,
      drops,
    },
  };
}

// ---------------------------------------------------------------------------
// Words: the summary the dialog's footer, the toast, the email and the audit
// all quote — "Business monthly · 15% off for 12 months · free until Oct 31".
// ---------------------------------------------------------------------------

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Oct 31" */
export const shortDay = (day: string): string => `${MON[Number(day.slice(5, 7)) - 1]} ${Number(day.slice(8, 10))}`;
/** "Oct 31, 2026" */
export const longDay = (day: string): string => `${shortDay(day)}, ${day.slice(0, 4)}`;

/** "$129", "$109.65", "$1,238.40" — whole dollars without cents. */
export function moneyWords(cents: number, currency = 'usd'): string {
  const symbol = currency.toLowerCase() === 'usd' ? '$' : `${currency.toUpperCase()} `;
  const whole = cents % 100 === 0;
  return symbol + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
}

export const perUnit = (interval: Interval): string => (interval === 'year' ? '/yr' : '/mo');

/** "15% off for 12 months", "15% off until Oct 1, 2027", "15% off forever", "Agreed $1,200/yr · MSA-031", "List price". */
export function pricingWords(p: PriceTerms, interval: Interval, currency = 'usd'): string {
  if (p.mode === 'discount') {
    const d = p.duration;
    return `${p.percent}% off ${d === 'forever' ? 'forever'
      : 'months' in d ? `for ${d.months} month${d.months === 1 ? '' : 's'}` : `until ${longDay(d.until)}`}`;
  }
  if (p.mode === 'agreed') return `Agreed ${moneyWords(p.cents, currency)}${perUnit(interval)}${p.note ? ` · ${p.note}` : ''}`;
  return 'List price';
}

export function summaryOf(t: BillingTerms, ctx: TermsContext): string {
  const plan = planOf(ctx, t.planId);
  const name = plan?.name ?? 'That plan';
  return [
    `${name} ${t.interval === 'year' ? 'annual' : 'monthly'}`,
    t.price.mode === 'list' ? 'list price' : pricingWords(t.price, t.interval, plan?.currency),
    t.freePeriod ? `free until ${shortDay(t.freePeriod.endsOn)}` : '',
  ].filter(Boolean).join(' · ');
}
